import { Link } from 'react-router-dom';
import { ArrowRight, Users, MapPin, Clock, Building2, Briefcase, TrendingUp, Check, Zap, Globe, Shield, Wallet, Mountain, Train, Landmark, GraduationCap, Music, Grape, Factory, Castle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { SEO } from '../../components/SEO';

export default function SuissePage() {
  return (
    <main data-testid="seo-page-suisse" className="pt-32">
      <SEO 
        title="Création de site web en Suisse Romande | Genève, Lausanne, Montreux, Nyon"
        description="Webdesigner freelance pour la Suisse Romande. Création de sites web à Genève, Lausanne, Montreux, Nyon, Vevey. Qualité suisse, tarifs français. Devis gratuit sous 48h."
        path="/creation-site-web-suisse"
      />

      {/* Hero Section */}
      <section className="container-main mb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="/images/seo/suisse.png" 
                alt="Suisse" 
                className="w-10 h-10 object-contain"
              />
              <span className="text-sm uppercase tracking-widest font-bold text-primary">
                Suisse Romande
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight mb-6">
              Création de site web
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-800 pb-2">
                en Suisse Romande
              </span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8">
              Webdesigner freelance basé en France, j'accompagne les <strong>entrepreneurs, startups et indépendants</strong> de <strong>Genève</strong>, <strong>Lausanne</strong> et <strong>Montreux</strong>. Qualité suisse, tarifs français.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/tarifs#contact">
                <Button className="bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 text-white text-lg px-8 py-6">
                  Discuter de mon projet
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/tarifs">
                <Button variant="outline" className="text-lg px-8 py-6">
                  Voir les tarifs
                </Button>
              </Link>
            </div>
          </div>

          {/* Image Jet d'eau Genève */}
          <div className="animate-fade-up animation-delay-200">
            <img 
              src="/images/seo/geneve.webp"
              alt="Création site web Suisse Romande - Jet d'eau de Genève"
              className="w-full h-auto rounded-2xl object-cover shadow-2xl"
            />
            <p className="text-xs text-muted-foreground mt-2 text-right">© Freepik</p>
          </div>
        </div>
      </section>

      {/* Section: Qualité suisse, tarifs français */}
      <section className="bg-muted/30 section-padding">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Stats Card */}
            <div className="order-2 lg:order-1">
              <Card className="overflow-hidden border-2 bg-gradient-to-br from-red-600 to-red-800 text-white">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6">Pourquoi travailler avec un freelance français ?</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <Check className="h-6 w-6 text-green-300 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold">Tarifs compétitifs</div>
                        <div className="text-sm text-red-100">Économisez jusqu'à 50% sur votre projet web</div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="h-6 w-6 text-green-300 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold">Même fuseau horaire</div>
                        <div className="text-sm text-red-100">Communication fluide, réunions en direct</div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="h-6 w-6 text-green-300 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold">Proximité géographique</div>
                        <div className="text-sm text-red-100">Je me déplace à Genève, Lausanne, Montreux</div>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="h-6 w-6 text-green-300 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold">Qualité professionnelle</div>
                        <div className="text-sm text-red-100">Standards européens, code propre, SEO optimisé</div>
                      </div>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-red-100 dark:bg-red-950/50 text-red-700 dark:text-red-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Shield className="h-4 w-4" />
                L'avantage frontalier
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Webdesigner freelance<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-800">
                  pour la Suisse Romande
                </span>
              </h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                En Suisse, un site web professionnel peut facilement coûter <strong>10 000 à 20 000 CHF</strong>. 
                Les agences de <strong>Genève</strong> et <strong>Lausanne</strong> ont des frais de structure énormes.
              </p>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Basé en France, à proximité de la frontière, je vous propose la <strong>même qualité de prestation</strong> avec 
                des tarifs jusqu'à <strong>50% moins chers</strong>. Création de site web à <strong>Genève</strong>, <strong>Lausanne</strong>, <strong>Montreux</strong>.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-card rounded-xl border">
                  <Wallet className="h-8 w-8 text-red-600 shrink-0" />
                  <div>
                    <div className="font-bold">-50%</div>
                    <div className="text-sm text-muted-foreground">vs agences suisses</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-card rounded-xl border">
                  <Globe className="h-8 w-8 text-red-600 shrink-0" />
                  <div>
                    <div className="font-bold">Bilingue</div>
                    <div className="text-sm text-muted-foreground">FR / EN</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-card rounded-xl border">
                  <Train className="h-8 w-8 text-red-600 shrink-0" />
                  <div>
                    <div className="font-bold">Mobilité</div>
                    <div className="text-sm text-muted-foreground">Déplacement sur site</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-card rounded-xl border">
                  <Zap className="h-8 w-8 text-red-600 shrink-0" />
                  <div>
                    <div className="font-bold">Réactif</div>
                    <div className="text-sm text-muted-foreground">Réponse sous 24h</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Pour qui */}
      <section className="section-padding">
        <div className="container-main">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-widest font-bold text-primary mb-4 block">
              Pour qui ?
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Création de site web pour les professionnels<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-800">
                en Suisse Romande
              </span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Entrepreneurs, startups, indépendants, frontaliers à Genève, Lausanne, Montreux... Un site web qui convertit, à prix maîtrisé.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Card: Entrepreneurs */}
            <Card className="group hover:border-red-500/50 transition-all hover:shadow-lg border-2">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-white mb-5 group-hover:scale-110 transition-transform">
                  <TrendingUp className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold mb-2">Entrepreneurs</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Lancez votre activité avec un site professionnel qui inspire confiance.
                </p>
                <ul className="space-y-1.5 text-xs">
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-green-500" />
                    Site vitrine pro
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-green-500" />
                    Formulaire de contact
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Card: Startups */}
            <Card className="group hover:border-red-500/50 transition-all hover:shadow-lg border-2">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center text-white mb-5 group-hover:scale-110 transition-transform">
                  <Zap className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold mb-2">Startups</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  MVP, landing pages, apps web. Lancez vite sans exploser votre budget.
                </p>
                <ul className="space-y-1.5 text-xs">
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-green-500" />
                    Landing page rapide
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-green-500" />
                    Optimisé conversion
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Card: Indépendants */}
            <Card className="group hover:border-red-500/50 transition-all hover:shadow-lg border-2">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white mb-5 group-hover:scale-110 transition-transform">
                  <Briefcase className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold mb-2">Indépendants</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Consultants, coachs, freelances. Démarquez-vous et générez des leads.
                </p>
                <ul className="space-y-1.5 text-xs">
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-green-500" />
                    Personal branding
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-green-500" />
                    Prise de RDV en ligne
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Card: Frontaliers */}
            <Card className="group hover:border-red-500/50 transition-all hover:shadow-lg border-2">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white mb-5 group-hover:scale-110 transition-transform">
                  <Building2 className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold mb-2">Frontaliers</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Vous travaillez en Suisse mais résidez en France ? Je comprends vos besoins.
                </p>
                <ul className="space-y-1.5 text-xs">
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-green-500" />
                    Facturation flexible
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-green-500" />
                    Proximité garantie
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section: Villes couvertes */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white section-padding">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Création de site web dans toute la Suisse Romande
            </h2>
            <p className="text-slate-300 max-w-2xl mx-auto">
              Genève, Lausanne, Montreux, Nyon, Vevey, Fribourg... Je me déplace pour vous rencontrer.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: 'Genève', desc: 'Finance, ONG, organisations internationales', Icon: Landmark },
              { name: 'Lausanne', desc: 'Tech, EPFL, startups innovantes', Icon: GraduationCap },
              { name: 'Montreux', desc: 'Tourisme, événementiel, hôtellerie', Icon: Music },
              { name: 'Nyon', desc: 'Entre lac et vignobles', Icon: Grape },
              { name: 'Vevey', desc: 'Siège de Nestlé, multinationales', Icon: Factory },
              { name: 'Fribourg', desc: 'Université, patrimoine historique', Icon: Castle },
            ].map((zone) => (
              <Card key={zone.name} className="bg-white/5 border-white/10 hover:bg-white/10 transition-colors">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center shrink-0">
                    <zone.Icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white">{zone.name}</h3>
                    <p className="text-sm text-slate-400">{zone.desc}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Statistiques Suisse Romande */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-6 bg-white/5 rounded-xl">
              <div className="text-3xl font-black text-red-400">2 M+</div>
              <div className="text-sm text-slate-400">habitants</div>
            </div>
            <div className="text-center p-6 bg-white/5 rounded-xl">
              <div className="text-3xl font-black text-red-400">6</div>
              <div className="text-sm text-slate-400">cantons francophones</div>
            </div>
            <div className="text-center p-6 bg-white/5 rounded-xl">
              <div className="text-3xl font-black text-red-400">1h30</div>
              <div className="text-sm text-slate-400">de Bourg-en-Bresse</div>
            </div>
            <div className="text-center p-6 bg-white/5 rounded-xl">
              <div className="text-3xl font-black text-red-400">Forfait</div>
              <div className="text-sm text-slate-400">Devis gratuit sous 48h</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="section-padding bg-muted/30">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Votre site web en Suisse Romande
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Création de site internet à Genève, Lausanne, Montreux ou ailleurs en Suisse Romande. Devis gratuit sous 48h.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/tarifs#contact">
                <Button className="bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 text-white text-lg px-8 py-6 w-full sm:w-auto">
                  Demander un devis gratuit
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/processus">
                <Button variant="outline" className="text-lg px-8 py-6 w-full sm:w-auto">
                  Découvrir mon processus
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>

            <div className="flex items-center justify-center gap-6 text-muted-foreground">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                <span>Suisse Romande</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span>Devis sous 48h</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
